/* ═══════════════════════════════════════════════════════════════
   BLOCK GROUPS — the two pure rules every editor path shares.

   A group is a shared `groupId` on a CONTIGUOUS run of top-level blocks
   (spec-studio/commands.ts, "GROUPS"). Two things keep that true no
   matter which gesture is acting:

     · a copy never joins the group it came from — every path that lands
       blocks carrying group ids (block paste, module insert) re-mints
       them through ONE remap per landing, so two landings of the same
       source give two independent groups (`createGroupRemap`);
     · nothing lands BETWEEN two blocks of one group — a drop line, a
       drag-move and an automatic placement all resolve their boundary
       through `snapBoundaryOutOfGroup`.

   Pure on purpose (no ProseMirror, no DOM) so the gutter drag, the
   document move and the module insert can never disagree about it, and
   a unit test can pin it down.
   ═══════════════════════════════════════════════════════════════ */

/**
 * One remap for ONE landing: every distinct source group gets one fresh id,
 * minted on first sight; blocks with no group stay ungrouped (''). Call this
 * once per paste / per module insert, never once per block — a group of
 * three must land as one group of three.
 */
export function createGroupRemap(
  mint: () => string
): (source: string | null | undefined) => string {
  const fresh = new Map<string, string>();
  return (source) => {
    const key = (source || '').trim();
    if (!key) return '';
    let id = fresh.get(key);
    if (!id) {
      id = mint();
      fresh.set(key, id);
    }
    return id;
  };
}

/** A top-level block as the boundary rule reads it. */
export interface GroupRow {
  id: string;
  /** '' = ungrouped. */
  group: string;
}

/**
 * The boundary a drop at `index` may actually use — the gap before row
 * `index`, `rows.length` meaning the end. A boundary between two rows of the
 * same group is NOT a drop target:
 *
 *   · `prefer: 'nearest'` (every pointer drag) — the group's nearer edge,
 *     the start on a tie, so the line jumps to where the eye already is;
 *   · `prefer: 'after'` (automatic placement) — always just after the group,
 *     so a landing computed as "after this block" stays after it;
 *   · `prefer: 'before'` (a one-step move upward) — always the group's start.
 *
 * The snap only guards a group the travelling blocks are NOT members of: if
 * any row of the group is itself travelling (`moving`), the drop splits
 * nothing of anybody else's and stands (a group dropped back into its own
 * gap, or a member moved within its own run). `legal`, when given, names
 * boundaries that are always a target whatever group surrounds them — the
 * module editor's "directly under an article heading" line (TWEAKS item 10).
 */
export function snapBoundaryOutOfGroup(
  rows: GroupRow[],
  index: number,
  opts: {
    moving?: Set<string>;
    prefer?: 'nearest' | 'after' | 'before';
    legal?: (index: number) => boolean;
  } = {}
): number {
  if (index <= 0 || index >= rows.length) return index;
  const gid = rows[index - 1].group;
  if (!gid || rows[index].group !== gid) return index;
  if (opts.legal?.(index)) return index;
  let start = index - 1;
  let end = index;
  while (start > 0 && rows[start - 1].group === gid) start--;
  while (end < rows.length - 1 && rows[end + 1].group === gid) end++;
  const moving = opts.moving;
  if (moving) {
    for (let i = start; i <= end; i++) if (moving.has(rows[i].id)) return index;
  }
  if (opts.prefer === 'after') return end + 1;
  if (opts.prefer === 'before') return start;
  return index - start <= end + 1 - index ? start : end + 1;
}
